import { cva } from 'class-variance-authority';
import {
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useId,
} from 'react';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { Tooltip } from './Tooltip';

/**
 * The one button (`FE-04.10`). Resolved against `B3-BRIEF.md` §4.
 *
 * ── `toggle` and `segment` are both kept, and they are not the same control ──
 * Two Stage 2 agents rejected a `SegmentedControl` primitive and proposed two
 * *different* replacements, because they were looking at two different things.
 *
 *  - `toggle` is **one control with two states**, carrying `aria-pressed` — the
 *    notification drawer's "Unread only" filter. `aria-pressed` says it; a
 *    widget would say it worse.
 *  - `segment` is **two links where one is current**, rendered `as='a'` with
 *    `aria-current='page'` — the landing page's Open/Closed control, a link
 *    because `?state=` lives in the URL. Collapsing it into `toggle` would make
 *    a URL a widget state.
 *
 * ── `busy` keeps the label, and the button never shrinks ──────────────────
 * Both returns insist on this independently, and the failure they are guarding
 * against is the same: a button that replaces "Create board" with a spinner has
 * thrown away what it was going to do and jumped half a column narrower while a
 * member's pointer was over it. So `busy` sets `aria-busy`, leaves the label
 * exactly where it was, and adds a `role='status'` string beside it.
 *
 * ⚠ **The spinner slot is rendered only while `busy` is true** (or when an
 * `iconStart` occupies it). It used to be reserved whenever the caller passed
 * `busy` at all, even `false`, so the width was identical in both states — and
 * every idle submit button in the product (90-odd files declare `busy`) carried
 * an empty 16px slot plus the 8px gap before its label, which read as a large
 * left padding and an off-centre label. The trade now made: the button
 * **grows** by the slot while in flight. Growing never takes the control out
 * from under a pointer that was over it — shrinking did, which is the failure
 * above — so the guarantee kept is "never narrower", not "never different".
 * The `role='status'` region is still present whenever `busy` is declared: it
 * is `sr-only`, costs no layout, and must exist before it has anything to say.
 *
 * `busy` deliberately does **not** set native `disabled`: a focused control that
 * becomes `disabled` drops focus to `<body>` mid-submit. `aria-disabled` plus a
 * click guard keeps it the same control throughout.
 *
 * ── `disabledReason` ──────────────────────────────────────────────────────
 * The cross-cutting rule: **a capability the viewer does not hold is locked with
 * a stated reason, not hidden** (`00-`, `FR-PERM-012` AC1). The reason renders
 * through `Tooltip` **and** `aria-describedby`, because a reason only a mouse
 * can reach is not a reason. `Tooltip` ships the disabled-trigger wrapper, so
 * this file does not re-implement it — content flows in, ids never flow out.
 *
 * ⚠ This primitive never *decides* a permission. There is no wire signal for
 * "may this viewer edit this card" (B3-BRIEF §7); `disabled` and
 * `disabledReason` arrive from the caller and nothing here infers them.
 */

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-2 rounded-card text-center font-medium',
    'transition-[filter,background-color,color]',
    'disabled:cursor-not-allowed disabled:opacity-60',
    'aria-disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-accent text-accent-ink',
          // A filter, not a second colour: there is no hover token, and
          // inventing one here is how a palette drifts. It darkens in light
          // theme and lightens in dark, which is the direction that RAISES
          // contrast against `--color-accent-ink` in each.
          'hover:brightness-95 dark:hover:brightness-110',
        ],
        secondary:
          'border border-border-strong bg-surface-raised text-ink hover:brightness-95 dark:hover:brightness-110',
        // `quiet` over `ghost`/`subtle`: it names the intent, not the rendering.
        quiet: 'text-ink hover:bg-surface-raised',
        danger: [
          // Card back's measurement: `--color-bad` filled with
          // `--color-accent-ink` reads 5.58:1 in light, and the dark half of
          // both tokens was re-measured in `styles.css`.
          'bg-bad text-accent-ink',
          'hover:brightness-95 dark:hover:brightness-110',
        ],
        toggle: [
          'border border-border-strong bg-surface-raised text-ink',
          'aria-pressed:border-accent aria-pressed:bg-accent aria-pressed:text-accent-ink',
        ],
        segment: [
          // Opaque, so the non-current segment may legally be muted text —
          // `--color-ink-muted` needs 98% and the panel floor is 92%.
          'bg-surface-raised text-ink-muted',
          'aria-[current=page]:text-ink aria-[current=page]:border-b-2 aria-[current=page]:border-accent',
          'hover:text-ink',
        ],
      },
      // A MINIMUM height, not a height. A label that outgrows its row — a
      // tool name interpolated into "Revoke {toolName}" — wraps, and with a
      // fixed `h-*` the second line spilled out of the top and bottom of the
      // pill. Now it grows instead; a single-line button is the same 28px /
      // 36px it always was (`density.test.ts` pins `sm`). A caller that sets
      // its own smaller `h-*` must set the matching `min-h-*` too.
      size: {
        sm: 'min-h-7 px-2 py-1 text-sm leading-tight',
        md: 'min-h-9 px-3 py-1.5 text-card leading-tight',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

interface ButtonOwnProps {
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger' | 'toggle' | 'segment';
  size?: 'sm' | 'md';
  /** In flight. Never changes the label and never shrinks the button. */
  busy?: boolean;
  /** What the `role='status'` string says while `busy`. */
  busyLabel?: string;
  /**
   * Why this control is refused. Rendered as a tooltip **and** as the control's
   * `aria-describedby`. Meaningless without `disabled`.
   */
  disabledReason?: string | undefined;
  /** A slot. Rendered `aria-hidden` — the label carries the name. */
  iconStart?: ReactNode;
  /** `variant='toggle'` only. Becomes `aria-pressed`. */
  pressed?: boolean;
  /**
   * Declared here rather than inherited, because `as='a'` has to accept it too:
   * an anchor has no native `disabled`, and a refused link that silently kept
   * its `href` would be a lock with the door open.
   */
  disabled?: boolean;
  className?: string;
  /**
   * Classes for the `<span>` that wraps `children`. That span is the button's
   * one content flex item and sizes to its content, so a child meant to *fill*
   * the button (`size-full`, `h-full`), a column meant to stack (`flex-col` on
   * the button never reaches the children), or a `truncate` label all collapse
   * or overflow inside it. A caller laying out more than a label styles the
   * wrapper here — `flex h-full min-w-0 flex-1 items-center` for a thumbnail
   * row, `flex min-w-0 flex-col items-center` for a tile — rather than nesting
   * a second layout box the wrapper then ignores.
   */
  contentClassName?: string | undefined;
}

type ButtonAsButton = ButtonOwnProps &
  Omit<ComponentPropsWithoutRef<'button'>, 'ref'> & {
    as?: 'button';
    ref?: Ref<HTMLButtonElement>;
  };

type ButtonAsAnchor = ButtonOwnProps &
  Omit<ComponentPropsWithoutRef<'a'>, 'ref'> & {
    as: 'a';
    ref?: Ref<HTMLAnchorElement>;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

type WidenedProps = ButtonOwnProps &
  Omit<ComponentPropsWithoutRef<'button'>, 'ref'> &
  Omit<ComponentPropsWithoutRef<'a'>, 'ref'> & {
    as?: 'button' | 'a';
    ref?: Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>;
  };

export function Button(props: ButtonProps) {
  const {
    variant = 'secondary',
    size = 'md',
    busy,
    busyLabel: busyLabelProp,
    disabledReason,
    iconStart,
    pressed,
    className,
    contentClassName,
    as = 'button',
    children,
    disabled,
    onClick,
    type,
    ref,
    'aria-pressed': ariaPressedProp,
    'aria-describedby': describedByProp,
    ...rest
  } = props as WidenedProps;

  const { t } = useInterfaceLanguage();
  const reactId = useId();
  const reasonId = `${reactId}-reason`;
  const busyLabel = busyLabelProp ?? t('primitives.button.busy');

  const isRefused = disabled === true;
  const showReason = disabledReason !== undefined && isRefused;

  const describedBy =
    [showReason ? reasonId : null, describedByProp].filter(Boolean).join(' ') ||
    undefined;

  const ariaPressed =
    ariaPressedProp ?? (variant === 'toggle' ? (pressed ?? false) : undefined);

  // The live region exists whenever the caller has declared `busy` at all; the
  // visible slot only while there is something to show in it — see the
  // docstring for why an idle button no longer reserves it.
  const reservesBusySlot = busy !== undefined;
  const showsSlot = busy === true || iconStart !== undefined;

  function handleClick(event: MouseEvent<HTMLElement>) {
    if (busy === true) {
      // `aria-disabled`, not `disabled`, so the guard has to be real.
      event.preventDefault();
      return;
    }
    if (as === 'a' && isRefused) {
      event.preventDefault();
      return;
    }
    (onClick as ((e: MouseEvent<HTMLElement>) => void) | undefined)?.(event);
  }

  const content = (
    <>
      {showsSlot && (
        <span
          aria-hidden="true"
          className="flex size-4 shrink-0 items-center justify-center"
        >
          {busy === true ? <Spinner /> : iconStart}
        </span>
      )}
      <span className={contentClassName}>{children}</span>
      {reservesBusySlot && (
        /*
         * Beside the label, never instead of it — and present in the DOM before
         * it has anything to say. A live region that is *inserted* already
         * populated is announced unreliably; one whose text changes is not.
         * So this one keeps the declared-`busy` condition the visible slot
         * gave up.
         */
        <span role="status" className="sr-only">
          {busy === true ? busyLabel : ''}
        </span>
      )}
    </>
  );

  const shared = {
    ...rest,
    className: cn(buttonVariants({ variant, size }), className),
    'aria-busy': busy === true ? true : undefined,
    'aria-pressed': ariaPressed,
    'aria-describedby': describedBy,
    onClick: handleClick,
  };

  const control =
    as === 'a' ? (
      <a
        {...shared}
        ref={ref as Ref<HTMLAnchorElement>}
        /*
         * An anchor has no `disabled`, and a refused one loses its `href` — but
         * an `<a>` without `href` is role `generic` and is not tabbable, so the
         * naive spelling *hides* the control instead of locking it. `role` and
         * `tabIndex` put both back.
         *
         * The `tabIndex` is conditional on purpose: when `disabledReason` is
         * present, `Tooltip` wraps this in its own `tabIndex={0}` span (the
         * documented disabled-trigger workaround, B3-BRIEF §6), and adding a
         * second focus target here would make one refused control two tab
         * stops.
         */
        role={isRefused ? 'link' : undefined}
        tabIndex={isRefused && !showReason ? 0 : undefined}
        aria-disabled={isRefused || busy === true ? true : undefined}
        href={isRefused ? undefined : (rest as ComponentPropsWithoutRef<'a'>).href}
      >
        {content}
      </a>
    ) : (
      <button
        {...shared}
        ref={ref as Ref<HTMLButtonElement>}
        // Never a bare <button>: inside a form that is a submit.
        type={type ?? 'button'}
        disabled={isRefused}
        aria-disabled={busy === true ? true : undefined}
      >
        {content}
      </button>
    );

  if (!showReason) return control;

  return (
    <>
      <Tooltip content={disabledReason}>{control}</Tooltip>
      {/*
        The `aria-describedby` half. It is our own node with our own id — the
        tooltip's content lives in a portal under an id this file does not own,
        and reaching for it would invent a contract §9 does not have.
      */}
      <span id={reasonId} className="sr-only">
        {disabledReason}
      </span>
    </>
  );
}

/** Private to this file; `IconButton` carries its own so neither imports the other. */
function Spinner() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-4 motion-safe:animate-spin"
      focusable="false"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.3"
      />
      <path
        d="M8 2a6 6 0 0 1 6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
